import { ApexCharts, ApexChartWidget } from './apex-chart-widget';
import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import {
  currencyCodeToString,
  moneyToCompactStr,
  stringToCurrencyCode
} from '../../../../webmodule-common/other/currency-formatter';
import { customElement, property } from 'lit/decorators.js';
import { getQuoteStatusStr } from '../../../quotes/data/quote-helper-functions';
import { issuedQuotesCache } from '../data/issued-quotes-cache';
import { LangIssuedQuotesWidget } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/issued-quotes-widget-lang';
import { localDateTimeToServer } from '../../../../webmodule-common/other/datetime-converter';
import { NullPromise } from '../../../../webmodule-common/interop/webmodule-interop';
import { QuoteState, ResultGetIssuedQuoteRevenue } from '../../../api/dealer-api-interface-quote';

@customElement('issued-quotes-revenue-widget')
export class GeneratedQuotesWidget extends ApexChartWidget {
  chart: any | null = null;
  // Track all per-currency charts to be able to destroy/clear on refresh
  private charts: any[] = [];
  private _isDisconnected = false;

  @property({ type: Array }) states: QuoteState[] = [
    QuoteState.Draft,
    QuoteState.Active,
    QuoteState.IssuePending,
    QuoteState.Issued,
    QuoteState.Accepted,
    QuoteState.Accepted_AssignedToReviewer,
    QuoteState.SupplierReviewPending,
    QuoteState.SupplierReviewed,
    QuoteState.Approved
  ];

  private _cache = issuedQuotesCache();
  constructor() {
    super('quote-issued-revenue');
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._isDisconnected = true;
    try {
      this.chart?.destroy?.();
    } catch {}
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch {}
    }
    this.charts = [];
  }

  protected async refreshData(): NullPromise<ResultGetIssuedQuoteRevenue> {
    if (this.dateRange) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const result = await this._cache?.getData(startDate, endDate, this.states, this.branchId, this.userId);
      if (result) {
        return result;
      }
    }
    return null;
  }

  protected getTitle(): string {
    return LangIssuedQuotesWidget.quoteNetAmount({ label: this.dateRange?.label ?? '' });
  }

  protected getSeriesColorByState(state: number): string {
    // return the corresponding badge colour
    switch (state as QuoteState) {
      case QuoteState.Draft:
        return '#0ea5e9';
      case QuoteState.Active:
        return '#d946ef';
      case QuoteState.IssuePending:
      case QuoteState.SupplierReviewPending:
        return '#a855f7';
      case QuoteState.Issued:
        return '#eab308';
      case QuoteState.SupplierReviewed:
        return '#06b6d4';
      case QuoteState.Accepted:
      case QuoteState.Accepted_AssignedToReviewer:
        return '#84cc16';
      case QuoteState.Approved:
        return '#f97316';
      default:
        return '#3282cc'; // base colour
    }
  }

  protected async updateData(): Promise<void> {
    const results = await this.refreshData();
    if (!results) return;

    await this.updateComplete;
    if (!this.isConnected || this._isDisconnected) return;

    // Destroy any existing charts and clear container before rendering new ones
    try {
      this.chart?.destroy?.();
    } catch {}
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch {}
    }
    this.charts = [];
    const parent = this.querySelector('.quote-issued-revenue');
    if (!parent || !this.isConnected || this._isDisconnected) return;
    parent.innerHTML = '';

    // Group revenueSummary by currency
    const currencyGroups: Record<string, typeof results.revenueSummary> = {} as any;
    for (const item of results.revenueSummary) {
      const currency = currencyCodeToString(item.currency) ?? 'USD';
      (currencyGroups[currency] ??= []).push(item);
    }

    // Two-phase render: build every currency container first (phase 1) so the
    // CSS grid settles to its final column count/width BEFORE any chart measures
    // itself (phase 2). Rendering a chart while later containers are still
    // missing makes ApexCharts bake in the full-card width, which then overflows
    // once the grid splits into columns (the "bleeding" overlap between
    // currencies).
    const pending: { container: HTMLDivElement; options: any }[] = [];
    Object.entries(currencyGroups).forEach(([currency, revenueSummary]) => {
      if (!this.isConnected || this._isDisconnected) return;

      const chartSeries: ChartSeriesXY[] = [];
      for (const quoteState of results.quoteStates) {
        const chartData: ChartDataXY[] = [];
        const data = revenueSummary.filter(d => d.state === quoteState || d.state === -1);
        data.forEach(r => chartData.push({ x: this.convertDateToMillis(r.date), y: r.dailyRevenue }));
        chartSeries.push({
          name: `${LangIssuedQuotesWidget.quoteStatus({ status: getQuoteStatusStr(quoteState) })}`,
          data: chartData,
          color: this.getSeriesColorByState(quoteState)
        });
      }

      const chartOptions = {
        chart: {
          type: 'area',
          height: '179px',
          selection: { enabled: false },
          stacked: false,
          toolbar: {
            tools: {
              download: false,
              customIcons: [
                {
                  icon: '<i class="fa fa-plus-circle fa-lg"></i>',
                  index: 0,
                  title: 'Zoom',
                  class: 'custom-icon',
                  click: (chart: any, options: any, e: any) => this.onChartClick(e, chart, options, currency)
                }
              ]
            }
          },
          zoom: { enabled: false }
        },
        stroke: { width: 1 },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } }
        },
        series: [],
        xaxis: { type: 'datetime' },
        yaxis: {
          labels: { formatter: (value: number) => `${moneyToCompactStr(value, stringToCurrencyCode(currency))}` }
        },
        noData: { text: 'Loading...' }
      } as any;

      // Create a container element under the main body container
      const parentEl = this.querySelector('.quote-issued-revenue');
      if (!parentEl || !this.isConnected || this._isDisconnected) return;
      const container = document.createElement('div');
      container.className = `quote-issued-revenue-currency`;
      const caption = document.createElement('div');
      caption.textContent = `${this.getTitle()} (${currency})`;
      caption.style.fontWeight = '600';
      caption.style.margin = '8px 0';
      container.appendChild(caption);
      parentEl.appendChild(container);

      pending.push({ container, options: { ...chartOptions, series: chartSeries } });
    });

    // Phase 2: every container is now in the grid at its final width, so each
    // chart measures its own column instead of the whole card.
    if (!this.isConnected || this._isDisconnected) return;
    for (const { container, options } of pending) {
      const chart = new ApexCharts(container, options);
      chart.render();
      this.charts.push(chart);
    }
  }
}
